import { ConvexClient } from "convex/browser";
import { defineNuxtPlugin, useRuntimeConfig } from "#app";
import { useAuthClient } from "#composables/useAuthClient";
import { createLogger } from "#bubba/shared/utils/logger";

const log = createLogger("ConvexPlugin", { enabled: true });

interface AuthClientWithCrossDomain {
  getCookie?: () => string;
}

/**
 * Nuxt plugin that initializes the Convex client with auth support.
 * The client is available via useNuxtApp().$convex
 */
export default defineNuxtPlugin(async () => {
  const config = useRuntimeConfig();
  const convexUrl = config.public.convexUrl as string;
  const convexSiteUrl = config.public.convexSiteUrl as string;

  let client: ConvexClient | null = null;

  const noop = () => { };

  if (!convexUrl) {
    log.warn("No NUXT_PUBLIC_CONVEX_URL configured");
    return {
      provide: {
        convex: null as ConvexClient | null,
        refreshConvexAuth: noop,
      },
    };
  }

  client = new ConvexClient(convexUrl);
  const authClient = useAuthClient() as unknown as AuthClientWithCrossDomain;

  const fetchToken = async () => {
    try {
      let cookie = '';

      if (typeof authClient?.getCookie === 'function') {
        const result = authClient.getCookie();
        if (typeof result === 'string' && result.trim()) {
          cookie = result;
        }
      }

      if (!cookie && typeof localStorage !== 'undefined') {
        const storedCookie = localStorage.getItem('better-auth_cookie');
        if (storedCookie) {
          try {
            const parsed = JSON.parse(storedCookie) as Record<
              string,
              { value?: string; expires?: string | null }
            >;
            cookie = Object.entries(parsed)
              .map(([key, val]) => {
                if (!val?.value) return '';
                return `${key}=${val.value}`;
              })
              .filter(Boolean)
              .join('; ');
          } catch {
            // ignore malformed cookie data
          }
        }
      }

      if (!cookie) {
        log.debug('No auth cookie available for Convex token fetch');
        return null;
      }

      const response = await fetch(`${convexSiteUrl}/api/auth/convex/token`, {
        credentials: 'include',
        headers: { 'Better-Auth-Cookie': cookie },
      });

      if (!response.ok) {
        log.warn('Convex token endpoint returned', response.status);
        return null;
      }

      const data = await response.json();
      return data?.token ?? null;
    } catch (err) {
      log.error('Failed to fetch Convex auth token', err);
      return null;
    }
  };

  client.setAuth(fetchToken);

  function refreshConvexAuth() {
    log.debug('Forcing Convex auth token refresh');
    client!.setAuth(fetchToken);
  }

  return {
    provide: {
      convex: client as ConvexClient | null,
      refreshConvexAuth,
    },
  };
});
